import createCachedSelector from 're-reselect';
import { connect } from 'react-redux';

import { ProductDetailsModalLoaderAndDataPassThrough } from './components/loader-and-data-passthrough';
import * as ProductDetailsDisplayHelpers from './display-helpers';
import { ProductDetailsService } from '../../services/product-details.service';
import Global from '../../store/global-selectors';
import { updateProduct, createProduct } from '../../store/products/products.actions';
import { SellerProperties } from '../../store/seller/seller-properties';

const productProfilesSelector = createCachedSelector(
  (productProfiles) => productProfiles,
  (_, sellerWeightUnit) => sellerWeightUnit,
  (_, __, sellerDimensionUnit) => sellerDimensionUnit,
  (profiles, weightUnit, dimensionUnit) => {
    return profiles.map((prof) => {
      const convertedWeight = ProductDetailsDisplayHelpers.convertWeightToDisplayUnit(weightUnit, prof.weight, prof.weightUnitOfMeasureId);
      const convertedDimensions = ProductDetailsDisplayHelpers.convertDimensionToDisplayUnit(dimensionUnit, {
        length: prof.length,
        width: prof.width,
        height: prof.height,
        unit: prof.dimensionUnitOfMeasureId,
      });

      return {
        ...prof,
        ...convertedDimensions,
        ...convertedWeight,
      };
    });
  },
)((productProfiles) => JSON.stringify(productProfiles.map((prof) => prof.productProfileId)));

const convertWeightAndDimensionsForParentProduct = (parentProduct, sellerWeightUnit, sellerDimensionUnit) => {
  if (!parentProduct) {
    return;
  }
  const { weight, weightUnitOfMeasureId, length, width, height, dimensionUnitOfMeasureId } = parentProduct;

  return {
    ...parentProduct,
    ...ProductDetailsDisplayHelpers.convertWeightToDisplayUnit(sellerWeightUnit, weight, weightUnitOfMeasureId),
    ...ProductDetailsDisplayHelpers.convertDimensionToDisplayUnit(sellerDimensionUnit, {
      length,
      width,
      height,
      unit: dimensionUnitOfMeasureId,
    }),
  };
};

const mapStateToProps = (state) => {
  const isLoading = Global.selectProductproductDetailsIsLoading(state);
  const productId = Global.selectProductproductDetailsProductId(state);
  if (isLoading) {
    return {
      isLoading,
      productId,
    };
  }

  const userHasPermission = Global.selectUserhasProductManagementRole(state);
  const product = ProductDetailsService.returnProductEntityFromMemory(state, productId) || {};
  const productProfiles = Global.selectProductproductProfiles(state);

  const sellerProperties = new SellerProperties({ state });
  const sellerWeightUnitSetting = sellerProperties.defaultWeightUnit;
  const sellerDimensionUnitSetting = sellerProperties.defaultDimensionUnit;

  const {
    name,
    sku,
    active,
    categoryId,
    fulfillmentSku,
    warehouseLocation,
    productType,
    useProductName,
    productProfileId,
    serviceId,
    providerId,
    packageTypeId,
    confirmationId,
    internalNotes,
    intlServiceId,
    intlProviderId,
    intlPackageTypeId,
    intlConfirmationId,
    domesticRateShopperGuid,
    intlRateShopperGuid,
    weight,
    weightUnitOfMeasureId,
    length,
    width,
    height,
    dimensionUnitOfMeasureId,
    parentProductId,
    thumbnailUrl,
    upc,
    noAutoCustomsDeclaration,
    customsCountry,
    customsDescription,
    customsTariffNo,
    customsMIDCode,
    customsValue,
    tags,
    isReturnable,
    modifiedDateUTC: modifiedDateUtc,
    createDateUTC: createDateUtc,
    hasHadInventory,
    productAliases,
    shouldTrackInventory,
    reorderThreshold,
    productVariantIds,
    autoSplitOption,
    productWarehouses,
    isBundle,
    bundleComponents,
    productOptions,
    autoSplitCustomQuantity,
    packagePreference,
  } = product;

  const { headerText, productTypeText } = ProductDetailsDisplayHelpers.getProductTypeDisplayNameAndHeaderText(productType);

  const parentProduct = ProductDetailsService.returnProductEntityFromMemory(state, parentProductId);

  return {
    userHasPermission,
    categoryId,
    productId,
    name: name || '',
    sku: sku || '',
    active,
    isReturnable,
    fulfillmentSku: fulfillmentSku || '',
    warehouseLocation: warehouseLocation || '',
    productType: productType || '',
    useProductName: !!useProductName,
    headerText,
    productTypeText,
    serviceId,
    providerId,
    packageTypeId,
    confirmationId,
    internalNotes,
    intlServiceId,
    intlProviderId,
    intlPackageTypeId,
    intlConfirmationId,
    domesticRateShopperGuid,
    intlRateShopperGuid,
    thumbnailUrl,
    tagIds: tags,
    upc,
    modifiedDateUtc,
    createDateUtc,
    hasHadInventory,
    shouldTrackInventory,
    reorderThreshold,
    noAutoCustomsDeclaration,
    customsCountry,
    customsDescription,
    customsTariffNo,
    customsMIDCode,
    customsValue,
    productAliases: productAliases || [],
    productVariantIds,
    autoSplitOption,
    parentProduct: convertWeightAndDimensionsForParentProduct(parentProduct),
    productProfiles: productProfilesSelector(productProfiles, sellerWeightUnitSetting, sellerDimensionUnitSetting),
    productProfileId: productProfileId || undefined,
    ...ProductDetailsDisplayHelpers.convertWeightToDisplayUnit(sellerWeightUnitSetting, weight, weightUnitOfMeasureId),
    ...ProductDetailsDisplayHelpers.convertDimensionToDisplayUnit(sellerDimensionUnitSetting, {
      length,
      width,
      height,
      unit: dimensionUnitOfMeasureId,
    }),
    productWarehouses,
    isBundle,
    bundleComponents,
    productOptions,
    autoSplitCustomQuantity,
    packagePreference,
  };
};

const mapDispatchToProps = {
  updateProduct,
  createProduct,
};

export const ProductDetails = connect(mapStateToProps, mapDispatchToProps)(ProductDetailsModalLoaderAndDataPassThrough);
